import { useState, useEffect } from "react";
import { NavLink, Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ShoppingBag } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useLang } from "@/i18n/LanguageContext";

const LINKS = [
  { to: "/", key: "home", id: "domu" },
  { to: "/o-mne", key: "about", id: "o-mne" },
  { to: "/dilna", key: "workshop", id: "dilna" },
  { to: "/spoluprace", key: "partnership", id: "spoluprace" },
  { to: "/zavody", key: "races", id: "zavody" },
  { to: "/media", key: "media", id: "media" },
  { to: "/shop", key: "shop", id: "shop" },
  { to: "/kontakt", key: "contact", id: "kontakt" },
];

const LangToggle = ({ compact = false }) => {
  const { lang, switchLang } = useLang();
  const base = compact
    ? "px-2.5 py-1 font-mono text-xs"
    : "px-2 py-0.5 font-mono text-[11px]";
  return (
    <div
      data-testid="lang-toggle"
      className="clip-corner-sm flex items-center border border-white/15 bg-[#0B0D10]/70"
      role="group"
      aria-label="Language"
    >
      <button
        data-testid="lang-toggle-cz"
        onClick={() => switchLang("cz")}
        aria-pressed={lang === "cz"}
        className={`${base} font-semibold uppercase tracking-[0.14em] transition-colors duration-200 ${
          lang === "cz" ? "bg-[#B7FF2A] text-[#07080A]" : "text-white/50 hover:text-white"
        }`}
      >
        CZ
      </button>
      <span className="h-4 w-px bg-white/15" aria-hidden="true" />
      <button
        data-testid="lang-toggle-en"
        onClick={() => switchLang("en")}
        aria-pressed={lang === "en"}
        className={`${base} font-semibold uppercase tracking-[0.14em] transition-colors duration-200 ${
          lang === "en" ? "bg-[#B7FF2A] text-[#07080A]" : "text-white/50 hover:text-white"
        }`}
      >
        EN
      </button>
    </div>
  );
};

export const HudNav = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const { count, setIsOpen } = useCart();
  const { t, lp } = useLang();
  const location = useLocation();

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  return (
    <>
      <div className="fixed inset-x-0 top-4 z-50 flex justify-center px-4">
        <motion.header
          data-testid="hud-nav"
          initial={{ y: -70, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1], delay: 0.2 }}
          className="w-full max-w-6xl"
        >
        <div className="hud-glass clip-corner-sm flex items-center justify-between gap-2 px-4 py-2.5 sm:px-5">
          {/* logo */}
          <Link
            to={lp("/")}
            data-testid="hud-nav-logo"
            className="group flex items-center gap-2"
          >
            <div className="clip-corner-sm h-8 w-8 bg-[#1EA7FF]" />
            <div className="font-display text-xl uppercase tracking-[0.1em] leading-none">
              <span className="rgb-split" data-text="FILIP VIMPEL">FILIP VIMPEL</span>
            </div>
            <span className="hidden font-mono text-[10px] tracking-[0.2em] text-[#B7FF2A] sm:block">
              #606
            </span>
          </Link>

          {/* desktop links */}
          <nav className="hidden items-center gap-0.5 lg:flex">
            {LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={lp(l.to)}
                end={l.to === "/"}
                data-testid={`hud-nav-link-${l.id}`}
                className={({ isActive }) =>
                  `relative px-3 py-1.5 font-display text-sm uppercase tracking-[0.14em] transition-colors duration-200 ${
                    isActive ? "text-[#B7FF2A]" : "text-white/70 hover:text-white"
                  }`
                }
              >
                {({ isActive }) => (
                  <>
                    {t(`nav.${l.key}`)}
                    {isActive && (
                      <motion.span
                        layoutId="nav-underline"
                        className="absolute inset-x-3 -bottom-0.5 h-[2px] bg-[#B7FF2A]"
                        transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                      />
                    )}
                  </>
                )}
              </NavLink>
            ))}
          </nav>

          {/* actions */}
          <div className="flex items-center gap-2">
            <div className="hidden sm:block">
              <LangToggle />
            </div>
            <button
              data-testid="cart-open-button"
              onClick={() => setIsOpen(true)}
              className="relative flex h-9 w-9 items-center justify-center rounded-md text-white/80 transition-colors duration-200 hover:bg-white/5 hover:text-white"
              aria-label={t("nav.openCart")}
            >
              <ShoppingBag className="h-5 w-5" />
              {count > 0 && (
                <span
                  data-testid="cart-count-badge"
                  className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-[#B7FF2A] px-1 font-mono text-[10px] font-semibold text-[#07080A]"
                >
                  {count}
                </span>
              )}
            </button>
            <button
              data-testid="hud-nav-mobile-toggle"
              onClick={() => setMobileOpen((v) => !v)}
              className="flex h-9 w-9 items-center justify-center rounded-md text-white/80 transition-colors duration-200 hover:bg-white/5 hover:text-white lg:hidden"
              aria-label="Menu"
            >
              {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>
        </motion.header>
      </div>

      {/* mobile overlay menu */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            data-testid="hud-nav-mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="fixed inset-0 z-40 flex flex-col items-center justify-center gap-2 bg-[#07080A]/95 backdrop-blur-xl lg:hidden"
          >
            <div className="scanlines pointer-events-none absolute inset-0" />
            {LINKS.map((l, i) => (
              <motion.div
                key={l.to}
                initial={{ opacity: 0, x: -30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.05 * i, duration: 0.3 }}
              >
                <NavLink
                  to={lp(l.to)}
                  end={l.to === "/"}
                  data-testid={`hud-nav-mobile-link-${l.id}`}
                  className={({ isActive }) =>
                    `font-display text-4xl uppercase tracking-[0.1em] ${
                      isActive ? "text-[#B7FF2A]" : "text-white/80"
                    }`
                  }
                >
                  {t(`nav.${l.key}`)}
                </NavLink>
              </motion.div>
            ))}
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.45, duration: 0.3 }}
              className="mt-6"
            >
              <LangToggle compact />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
